/**
 * Shared Framer Motion variants and transitions for consistent animations.
 * Respects reduced motion via MotionConfig (reducedMotion="user").
 */

const easeDefault = [0.32, 0.72, 0, 1] as const;

/** Default transition for overlay/panel (modals, drawers). */
export const transitionDefault = { duration: 0.25, ease: easeDefault };

/** Slightly faster for backdrops. */
export const transitionBackdrop = { duration: 0.2, ease: easeDefault };

/** For sliding panels (e.g. mobile nav). */
export const transitionPanel = { duration: 0.3, ease: easeDefault };

/** For list/card enter animations. */
const transitionEnter = { duration: 0.4, ease: easeDefault };

/** Fade in (opacity only). */
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: transitionBackdrop,
} as const;

/** Fade in + scale (e.g. modal panel). */
export const fadeInScale = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96 },
  transition: transitionDefault,
} as const;

/** Slide in from bottom (y: 20px). */
export const slideInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: transitionEnter,
} as const;

const STAGGER_MAX_INDEX = 8;
const STAGGER_STEP_S = 0.05;

/** Stagger delay for list items (capped so long lists do not feel sluggish). */
const staggerDelay = (index: number) => Math.min(index, STAGGER_MAX_INDEX) * STAGGER_STEP_S;

/** Variants for list item: slide in up with optional stagger. */
export function slideInUpWithStagger(index: number) {
  return {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { ...transitionEnter, delay: staggerDelay(index) },
  } as const;
}

/** Tab panel content crossfade (detail pages). */
export const tabContentTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.2, ease: easeDefault },
} as const;

/** Toast enter/exit (top on mobile, bottom on desktop). */
export const toastMotion = {
  initial: { opacity: 0, y: -16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: transitionDefault,
} as const;
